// ThreadPreview — the inline summary we tuck under a thread root in
// the main timeline. Quiet by default: a small avatar stack and a
// single line of status, indented under the root. Loud when it
// matters: an unread dot takes the row to `fg-primary` so the user
// catches it without scanning.
//
// Purely presentational. Live data is composed upstream via
// `useThread`; the click forwards through `onOpen`, which the row
// owner wires to the timeline's `onOpenThread(root)` callback —
// `Timeline.onOpenThread` is the single seam that both this preview
// and the hover-action "Reply" button funnel into so a row only ever
// has to know "open the thread anchored on me".

import { useState, type CSSProperties } from "react";

import { useAuthenticatedMedia } from "~/hooks/useAuthenticatedMedia";
import { getInitials } from "~/lib/initials";
import type { Client, ThreadSummary } from "~/lib/matrix";

const AVATAR_SIZE = 16;
const AVATAR_OVERLAP = 2;
const UNREAD_DOT_SIZE = 6;

function StackAvatar({
  client,
  avatarUrl,
  name,
  index,
}: {
  client: Client | null;
  avatarUrl: string | null;
  name: string;
  index: number;
}) {
  const mxc = avatarUrl?.startsWith("mxc://") ? avatarUrl : null;
  const media = useAuthenticatedMedia(client, mxc, {
    thumbnail: { w: AVATAR_SIZE * 2, h: AVATAR_SIZE * 2, method: "crop" },
  });
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  const style: CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: AVATAR_SIZE,
    height: AVATAR_SIZE,
    borderRadius: "var(--chat-radius-full)",
    background: "var(--chat-surface-raised)",
    color: "var(--chat-fg-secondary)",
    fontFamily: "var(--chat-font-sans)",
    fontWeight: 500,
    fontSize: 9,
    lineHeight: 1,
    border: "1px solid var(--chat-surface-app)",
    boxSizing: "border-box",
    marginLeft: index === 0 ? 0 : -AVATAR_OVERLAP,
    zIndex: index + 1,
    position: "relative",
    overflow: "hidden",
  };
  return (
    <span aria-hidden style={style}>
      {media.src && media.src !== failedSrc ? (
        <img
          src={media.src}
          alt=""
          onError={() => setFailedSrc(media.src)}
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
      ) : (
        (Array.from(getInitials(name))[0] ?? "")
      )}
    </span>
  );
}

export interface ThreadPreviewProps {
  summary: ThreadSummary;
  client: Client | null;
  /** Resolve a sender id to a displayable name. The hook can't do this
   *  on its own — display names live in room state. */
  resolveSenderName: (senderId: string) => string;
  /** Pre-formatted relative timestamp for the last reply, e.g. "2m ago". */
  lastReplyRelativeTime: string;
  /** When true, render the row with the unread dot and primary fg. */
  hasUnread?: boolean;
  /** Click handler; opens the `ThreadPanel` for this root. */
  onOpen: () => void;
}

/**
 * Inline preview rendered under a thread-root message in the main
 * timeline. Hidden by the caller when `summary.replyCount === 0` — a
 * thread with no replies has nothing to preview.
 */
export function ThreadPreview({
  summary,
  client,
  resolveSenderName,
  lastReplyRelativeTime,
  hasUnread = false,
  onOpen,
}: ThreadPreviewProps) {
  if (summary.replyCount === 0) return null;

  const replyCount = summary.replyCount;
  const lastReply = summary.lastReply;
  const lastSenderName = lastReply ? resolveSenderName(lastReply.sender.id) : "";
  const labelColor = hasUnread ? "var(--chat-fg-primary)" : "var(--chat-fg-secondary)";
  const replyLabel = replyCount === 1 ? "reply" : "replies";

  return (
    <button
      type="button"
      onClick={onOpen}
      // The visible label already carries the reply count, last-reply
      // author, and timestamp — let the screen reader read that rather
      // than an `aria-label` that masks the richer visible text.
      className="chat-thread-preview chat-button"
      style={{
        display: "flex",
        alignItems: "center",
        gap: "var(--chat-space-2)",
        width: "100%",
        marginTop: "var(--chat-space-2)",
        // On the message text's own edge — an indent here reads as a
        // second, unrelated column.
        paddingTop: "var(--chat-space-1)",
        paddingBottom: "var(--chat-space-1)",
        border: "none",
        background: "transparent",
        textAlign: "left",
        cursor: "pointer",
        transition: "background var(--chat-motion-fast) var(--chat-motion-ease)",
      }}
    >
      {hasUnread && (
        <span
          aria-hidden
          style={{
            width: UNREAD_DOT_SIZE,
            height: UNREAD_DOT_SIZE,
            borderRadius: "var(--chat-radius-full)",
            background: "var(--chat-unread-dot)",
            flexShrink: 0,
            // Hangs outside the text edge so the avatars never shift.
            marginLeft: `calc(-1 * (${UNREAD_DOT_SIZE}px + var(--chat-space-2)))`,
          }}
        />
      )}
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          flexShrink: 0,
          // Holds the first avatar's place so the count does not shift
          // sideways when the thread's senders arrive.
          minWidth: AVATAR_SIZE,
        }}
      >
        {summary.recentSenders.slice(0, 3).map((sender, i) => (
          <StackAvatar
            key={sender.id}
            client={client}
            avatarUrl={sender.avatarUrl}
            name={resolveSenderName(sender.id)}
            index={i}
          />
        ))}
      </span>
      <span
        style={{
          flex: 1,
          minWidth: 0,
          color: labelColor,
          fontFamily: "var(--chat-font-sans)",
          fontSize: "var(--chat-type-body-xs-size)",
          lineHeight: "var(--chat-type-body-xs-line)",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          fontWeight: 400,
        }}
      >
        <span style={{ color: "var(--chat-fg-primary)" }}>
          {replyCount} {replyLabel}
        </span>
        {lastReply && (
          <>
            <span aria-hidden style={{ opacity: 0.5, padding: "0 var(--chat-space-1)" }}>
              ·
            </span>
            Last reply from {lastSenderName}
            <span aria-hidden style={{ opacity: 0.5, padding: "0 var(--chat-space-1)" }}>
              ·
            </span>
            {lastReplyRelativeTime}
          </>
        )}
      </span>
    </button>
  );
}
